import { isHomepagePath, type Settings } from '../../../shared/types/user'
import { applyTheme } from '../../utils/theme'

type SettingsFormTarget = HTMLInputElement | HTMLSelectElement
type SettingsChangeResult = 'updated' | null

type InputSettingUpdater = (
	settings: Settings,
	target: HTMLInputElement,
) => SettingsChangeResult
type SelectSettingUpdater = (
	settings: Settings,
	target: HTMLSelectElement,
) => SettingsChangeResult

/** Returns a supported settings form target from a DOM event target. */
export function getSettingsFormTarget(
	target: EventTarget | null,
): SettingsFormTarget | null {
	return target instanceof HTMLInputElement ||
		target instanceof HTMLSelectElement
		? target
		: null
}

/** Applies a changed settings control to the provided mutable settings copy. */
export function applySettingsFormChange(
	settings: Settings,
	target: SettingsFormTarget,
): SettingsChangeResult {
	if (target instanceof HTMLInputElement) {
		return INPUT_SETTING_UPDATERS[target.id]?.(settings, target) ?? null
	}

	return SELECT_SETTING_UPDATERS[target.id]?.(settings, target) ?? null
}

const INPUT_SETTING_UPDATERS: Record<string, InputSettingUpdater> = {
	'theme-toggle': (settings, target) => {
		settings.theme = target.checked ? 'light' : 'dark'
		applyTheme(target.checked)
		return 'updated'
	},
	'show-collection-value-toggle': (settings, target) => {
		settings.showCollectionValue = target.checked
		return 'updated'
	},
	'skip-shuffle-animation-toggle': (settings, target) => {
		settings.skipShuffleAnimation = target.checked
		return 'updated'
	},
}

const SELECT_SETTING_UPDATERS: Record<string, SelectSettingUpdater> = {
	'homepage-select': (settings, target) => {
		if (!isHomepagePath(target.value)) return null
		settings.homepage = target.value
		return 'updated'
	},
	'hide-duration-select': (settings, target) => {
		settings.defaultHideDurationMonths = readNumberSelectValue(target)
		return 'updated'
	},
}

/** Reads a numeric select value, returning null for empty or invalid values. */
function readNumberSelectValue(
	selectEl: HTMLSelectElement | null,
): number | null {
	const value = selectEl?.value ?? ''
	if (!value) return null
	const numberValue = Number(value)
	return Number.isFinite(numberValue) ? numberValue : null
}
